import './SinglePatientPage.scss';

import React, { useEffect, useState } from 'react';
import { formatDate } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';

import BackLink from '@components/BackLink';
import Button, { ButtonStyle } from '@components/Button';
import DescriptionList from '@components/DescriptionList';
import { useInjection } from '@components/Injection';
import Layout from '@components/Layout';
import { Tab } from '@components/Tabs';
import Tabs from '@components/Tabs/Tabs';
import { defaultDateFormat } from '@constants/general';
import { RoutePath } from '@constants/route-path';
import { IBraceOrder } from '@modulate/shared/src/models/brace-order';
import { IPatient } from '@modulate/shared/src/models/patient';
import BraceOrderDetails from '@pages/components/BraceOrderDetails';
import { useAuth } from '@src/contexts/authContext';
import { BRACE_ORDERS_SERVICE, IBraceOrdersService, IPatientsService, PATIENTS_SERVICE } from '@src/services';

function SinglePatientPage() {
  const authContext = useAuth();
  const { t } = useTranslation();
  const { patientId } = useParams();

  const [patient, setPatient] = useState<IPatient>();
  const [braceOrders, setBraceOrders] = useState<IBraceOrder[]>();

  const patientsService = useInjection<IPatientsService>(PATIENTS_SERVICE);
  const braceOrdersService = useInjection<IBraceOrdersService>(BRACE_ORDERS_SERVICE);

  useEffect(() => {
    patientsService.getById(patientId, authContext.userObject.clinicId).then(setPatient);
    braceOrdersService.findAllForPatient(patientId, authContext.userObject.clinicId).then(setBraceOrders);
  }, [patientId]);

  if (!patient || !braceOrders) {
    return null;
  }

  const newBraceOrderLink = generatePath(RoutePath.NewBraceOrderPage, { patientId });
  return (
    <Layout>
      <BackLink to={RoutePath.PatientListPage}>{t('patientList.alternativeTitle')}</BackLink>
      <div className="modSinglePatientPage">
        <article className="modSinglePatientPage__infos">
          <h1 className="modTitle modTitle--h1">
            {patient.firstName} {patient.lastName}
          </h1>
          <DescriptionList
            items={[
              {
                title: t('models.patient.dateOfBirth'),
                value: formatDate(patient.dateOfBirth, defaultDateFormat),
              },
              { title: t('models.patient.email'), value: patient.email },
              { title: t('models.patient.phoneNumber'), value: patient.phoneNumber },
              {
                title: t('models.patient.insurance'),
                value:
                  patient.hasNoHealthInsurance || !patient.insuranceInfo
                    ? t('general.none')
                    : `${patient.insuranceInfo.companyName} (${patient.insuranceInfo.insuranceNumber})`,
              },
            ]}
          />
          <hr className="modSinglePatientPage__infos__separator" />
        </article>
        <div className="modSinglePatientPage__brace-orders">
          {braceOrders.length === 0 && (
            <article className="modSinglePatientPageEmptyPanel">
              <div className="modSinglePatientPageEmptyPanel__text">
                <h5 className="modTitle modTitle--h5">{t('singlePatient.noBraceOrderPanel.title')}</h5>
                <p className="modText modText--body1">{t('singlePatient.noBraceOrderPanel.description')}</p>
              </div>
              <Button type="link" to={newBraceOrderLink}>
                {t('singlePatient.noBraceOrderPanel.action')}
              </Button>
            </article>
          )}
          {braceOrders.length > 0 && (
            <>
              <Button type="link" style={ButtonStyle.Action} to={newBraceOrderLink}>
                <i className="modIcon modIcon--plus" /> {t('general.new')}
              </Button>
              <Tabs>
                {braceOrders.map((order) => (
                  <Tab key={order.id} name={order.braceInfo.name}>
                    <BraceOrderDetails order={order} />
                  </Tab>
                ))}
              </Tabs>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default SinglePatientPage;
